import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {MAX_NUMBER_OF_ANSWERS} from '../../../../constants';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Label from '../../../../incase-domain/components/label/Label';
import {IElementPhotoAnswerQuestion} from '../../../../incase-domain/elements/question/photoAnswerQuestion';
import {useSaveAppliedRulesMutation} from '../../../../hooks/useSaveAppliedRules';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {isRuleInFocus} from '../../../../utils/rule';
import {removeArrayElement, swapArrayElement} from '../../../../utils/utils';
import {TABS} from '../../form/part/tabs';
import ElementShadow from '../common/elementShadow';
import {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import {ImageFit} from '../common/imageFit';
import ChangeAnswer from './answers/actions/ChangeAnswer';
import AnswerActions, {AnswerActionType} from './answers/AnswerActions';
import Answers from './answers/Answers';
import AdvancedOptions from './components/advancedOptions/AdvancedOptions';
import QuestionOptions, {QUESTION_OPTIONS} from './components/QuestionOptions';
import QuestionText from './components/QustionText';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedPhotoAnswerQuestionElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedPhotoAnswerQuestionElementComponent(props: HighlightedPhotoAnswerQuestionElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {mutate: saveAppliedRules} = useSaveAppliedRulesMutation();
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementPhotoAnswerQuestion;
    const presentation = useSelector(selectedPresentationSelector);

    function addAnswer(): void {
        if (highlightedElement.answers.length < MAX_NUMBER_OF_ANSWERS) {
            const newAnswers = [
                ...highlightedElement.answers,
                {
                    text: `${t('presEditPage.editSidebar.elements.initialStates.answer')} ${highlightedElement.answers.length + 1}`,
                    type: highlightedElement.isDebatable ? 'debatable' : 'incorrect',
                    comment: '',
                },
            ];
            updateHighlightedElement.single('answers', newAnswers);
        } else {
            toast.info(`${t('toasts.maxAnswers')}`);
        }
    }

    function removeAnswer(index: number): void {
        const newAnswers = removeArrayElement(highlightedElement.answers, index);
        const updatedRules = presentation.appliedRules
            ? presentation.appliedRules.filter((r) => !isRuleInFocus(r, highlightedElement.id, index))
            : [];

        updateHighlightedElement.single('answers', newAnswers);
        saveAppliedRules({appliedRules: updatedRules, presentationId: presentation._id});
    }

    function layoutChangeHandler(newValue: number): void {
        updateHighlightedElement.single('answersInARow', newValue);
    }

    function answerChangeHandler(index: number, value: string): void {
        const changedAnswer = {...highlightedElement.answers[index]};
        changedAnswer.text = value;

        const newAnswers = swapArrayElement(highlightedElement.answers, index, changedAnswer);
        updateHighlightedElement.single('answers', newAnswers);
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <div className="l-group--form">
                    <div className="l-group__item">
                        <Label>{t('designSidepanel.noOfAnswers')}</Label>
                        <div className="l-group__item u-flex u-h-between">
                            <label htmlFor="2" className="u-flex u-v-center u-h-center" title="2">
                                <input
                                    id="2"
                                    type="radio"
                                    name="2"
                                    className="u-mt-0 u-mb-0"
                                    checked={highlightedElement.answersInARow === 2}
                                    value="2"
                                    onChange={() => layoutChangeHandler(2)}
                                />
                                2
                            </label>

                            <label htmlFor="3" className="u-flex u-v-center u-h-center" title="3">
                                <input
                                    id="3"
                                    type="radio"
                                    name="3"
                                    className="u-mt-0 u-mb-0"
                                    checked={highlightedElement.answersInARow === 3}
                                    value="3"
                                    onChange={() => layoutChangeHandler(3)}
                                />
                                3
                            </label>

                            <label htmlFor="4" className="u-flex u-v-center u-h-center" title="4">
                                <input
                                    id="4"
                                    type="radio"
                                    name="4"
                                    className="u-mt-0 u-mb-0"
                                    checked={highlightedElement.answersInARow === 4}
                                    value="4"
                                    onChange={() => layoutChangeHandler(4)}
                                />
                                4
                            </label>

                            <label htmlFor="6" className="u-flex u-v-center u-h-center" title="6">
                                <input
                                    id="6"
                                    type="radio"
                                    name="6"
                                    className="u-mt-0 u-mb-0"
                                    checked={highlightedElement.answersInARow === 6}
                                    value="6"
                                    onChange={() => layoutChangeHandler(6)}
                                />
                                6
                            </label>
                        </div>
                    </div>
                </div>
                <ImageFit highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
                <div className="u-mt-2">
                    <AdvancedOptions
                        highlightedElement={highlightedElement}
                        highlightedElementChangeHandler={updateHighlightedElement}
                        answerColorFields={[COLOR_FIELDS.CORRECT_COLOR, COLOR_FIELDS.INCORRECT_COLOR, COLOR_FIELDS.DEBATABLE_COLOR]}
                        commentColorFields={[
                            COLOR_FIELDS.CORRECT_COMMENT_COLOR,
                            COLOR_FIELDS.INCORRECT_COMMENT_COLOR,
                            COLOR_FIELDS.DEBATABLE_COMMENT_COLOR,
                        ]}
                    />
                </div>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <QuestionOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    questionOptions={[
                        QUESTION_OPTIONS.SHOW_INDEX,
                        QUESTION_OPTIONS.SINGLE_CORRECT,
                        QUESTION_OPTIONS.BLOCKING_QUESTION,
                        QUESTION_OPTIONS.SHOW_IN_REPORT,
                        QUESTION_OPTIONS.SHOW_IN_REPORT,
                        QUESTION_OPTIONS.SHOW_ALL_COMMENTS,
                        QUESTION_OPTIONS.RETRY_QUESTION,
                    ]}
                    maxNumberOfRetryAttempts={highlightedElement.singleCorrect ? highlightedElement.answers.length : 10}
                />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <QuestionText
                highlightedElement={highlightedElement}
                errors={errors}
                highlightedElementChangeHandler={updateHighlightedElement}
                showDebatable
            />

            <Answers errors={errors} addAnswer={addAnswer}>
                {highlightedElement.answers.map((answer, index) => (
                    <React.Fragment key={index}>
                        <ChangeAnswer
                            answerChangeHandler={answerChangeHandler}
                            validationErrors={errors}
                            answer={answer.text}
                            index={index}
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                        />
                        <AnswerActions
                            index={index}
                            validationErrors={errors}
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            actions={[
                                AnswerActionType.CHANGE_COMMENT,
                                AnswerActionType.REMOVE_ANSWER,
                                AnswerActionType.ANSWER_RULES,
                                AnswerActionType.CHANGE_PHOTO_ANSWER,
                            ]}
                            removeAnswer={removeAnswer}
                        />
                    </React.Fragment>
                ))}
            </Answers>
        </>
    );
}

export default HighlightedPhotoAnswerQuestionElementComponent;
